// Product gallery controls (Bitrix 12401) for the ProductWiz gallery (the Variant Image Wizard app draws it in JS
// with Swiper). Redesigned product templates only.
// - Desktop prev/next arrows that change the main photo (phones swipe) and a "3 / 70" position counter. The app's
//   own arrows only scroll the thumbnail strip, so they're hidden and its Swiper is driven directly.
// - Frame sizing: 4:3 photos, the render (any image that isn't 4:3) at its own ratio, fitted to the screen height.
// - Desktop: the gallery stays under the header while the buy box scrolls; when it's taller than the screen, the
//   mouse wheel over it slides the gallery up until the thumbnails show.
// - Thumbnail strip arrows, eased photo and strip motion, the active thumbnail centred.
// - Around it: the pre-order ribbon stays in the frame; the full-screen viewer gets the same arrows.
// The app rebuilds its Swiper on every variant change, so everything re-attaches.
//
// It relies on the app's markup, which isn't a contract (README rule 1). If an app update changes any of these, the
// feature degrades to the app's own gallery; nothing else on the page depends on it:
//   container.swiper and its params/events, .rio-media-gallery, .pwzrswiper-container/-slide/-wrapper,
//   .PwzrGrid-root.rio-hide-scrollbar (thumbnail strip), .PwzrGrid-item, [aria-label^="Scroll thumbnails"] (its strip
//   arrows), .pwwp__button--arrow--left/right (full-screen viewer). The CSS side is in product-redesigned.css.liquid.
(() => {
  if (!document.body.classList.contains('template-product_redesigned')) return;

  const style = document.createElement('style');
  style.textContent = `
    .awzz-gallery-counter{position:absolute;bottom:10px;left:10px;z-index:6;padding:3px 8px;border-radius:12px;
      background:rgba(0,0,0,.6);color:#fff;font:12px/1.4 Arial,sans-serif;pointer-events:none}
    .awzz-sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}
    .awzz-gallery-arrow,.awzz-strip-arrow{display:none}
    @media (hover:hover) and (pointer:fine) and (min-width:720px){
      .awzz-gallery-arrow{position:absolute;top:50%;z-index:6;display:grid;place-items:center;width:44px;height:44px;
        margin-top:-22px;padding:0;border:1px solid rgba(24,24,26,.08);border-radius:8px;background:rgba(255,255,255,.94);background-clip:padding-box;box-shadow:0 1px 6px rgba(17,20,23,.12);
        color:#18181a;cursor:pointer;transition:background .15s ease,transform .15s ease}
      .awzz-gallery-arrow:hover{background:#fff}
      .awzz-gallery-arrow:active{transform:scale(.96)}
      .awzz-gallery-arrow:disabled{opacity:.35;cursor:default}
      .awzz-gallery-prev{left:10px}.awzz-gallery-next{right:10px}
      .awzz-gallery-prev svg{transform:rotate(180deg)}
      /* Thumbnail strip arrows: frosted glass over the thumbnails. Phones swipe the strip. */
      .awzz-strip-arrow{position:absolute;top:0;bottom:0;z-index:2;display:grid;place-items:center;width:34px;padding:0;
        border:1px solid rgba(24,24,26,.08);border-radius:6px;background:rgba(255,255,255,.4);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);box-shadow:0 1px 6px rgba(17,20,23,.08);
        color:#18181a;cursor:pointer;transition:background .15s ease,transform .15s ease}
      .awzz-strip-arrow:hover{background:rgba(255,255,255,.7)}
      .awzz-strip-arrow:active{transform:scale(.96)}
      .awzz-strip-arrow[hidden]{display:none}
      .awzz-strip-prev{left:0}.awzz-strip-next{right:0}
      .awzz-strip-prev svg{transform:rotate(180deg)}
      /* Two-tone focus ring, visible on dark and light photos alike. */
      .awzz-gallery-arrow:focus-visible,.awzz-strip-arrow:focus-visible{outline:2px solid #fff;outline-offset:0;box-shadow:0 0 0 4px #1b4774}
    }
    @media (prefers-reduced-motion:reduce){.awzz-gallery-arrow,.awzz-strip-arrow{transition:none}}
    .rio-media-gallery [aria-label^="Scroll thumbnails"]{display:none!important}
    /* Variant change: the last photo stays under the frame and new photos fade in once loaded (no white blink). */
    .rio-media-gallery.awzz-fit.awzz-handover .pwzrswiper-container{background:#fff var(--awzz-last) center/contain no-repeat}
    .rio-media-gallery.awzz-fit .pwzrswiper-slide{background:transparent}
    .awzz-ghost{position:absolute;z-index:5;pointer-events:none;background:#fff;transition:opacity .2s ease}
    .awzz-ghost img{display:block;width:100%;height:100%;object-fit:contain}
    .awzz-ghost.awzz-ghost-out{opacity:0}
    .rio-media-gallery.awzz-fit.awzz-ghosting .pwzrswiper-slide img.awzz-loaded{transition:none}
    .rio-media-gallery.awzz-fit .pwzrswiper-slide img{transition:opacity .25s ease}
    .rio-media-gallery.awzz-fit .pwzrswiper-slide img:not(.awzz-loaded){opacity:0}
    @media (prefers-reduced-motion:reduce){.rio-media-gallery.awzz-fit .pwzrswiper-slide img{transition:none}}
    /* The gallery can slide up under the header (see the wheel handler); the announcement bars above the header are
       unpositioned, so give them a layer and the gallery passes under them too. */
    .shopify-section-group-header-group:not(.site-header-wrapper){position:relative;z-index:1}
    /* The app's full-screen viewer: the same 44px white arrows as the gallery instead of its sprite arrows. */
    .pwwp .pwwp__button--arrow--left,.pwwp .pwwp__button--arrow--right{opacity:1}
    .pwwp .pwwp__button--arrow--left::before,.pwwp .pwwp__button--arrow--right::before{top:28px;width:44px;height:44px;
      border:1px solid rgba(24,24,26,.08);border-radius:8px;box-shadow:0 1px 6px rgba(17,20,23,.12);
      background:rgba(255,255,255,.94) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M6 3.5L10.5 8 6 12.5' fill='none' stroke='%2318181a' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/18px no-repeat;
      transition:background-color .15s ease}
    .pwwp .pwwp__button--arrow--left::before{left:16px;right:auto;transform:rotate(180deg)}
    .pwwp .pwwp__button--arrow--right::before{right:16px;left:auto}
    .pwwp .pwwp__button--arrow--left:hover::before,.pwwp .pwwp__button--arrow--right:hover::before{background-color:#fff}
    .pwwp .pwwp__button--arrow--left:focus-visible::before,.pwwp .pwwp__button--arrow--right:focus-visible::before{outline:2px solid #fff;outline-offset:2px}`;
  document.head.append(style);

  const chevron = (size, stroke) => `<svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3.5L10.5 8 6 12.5" stroke="currentColor" stroke-width="${stroke}" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const CHEVRON = chevron(18, 1.8); // photo arrows
  const CHEVRON_SM = chevron(14, 1.6); // strip arrows

  const DESK = window.matchMedia('(min-width: 990px)');
  const PHONE = window.matchMedia('(max-width: 719px)');
  const FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  // The frame is sized for the collapsed header (66px), so it doesn't change size when the header collapses on scroll.
  const HDR = 66;
  const GAP = 16; // gap above the frame and under the thumbnails
  const RATIO = 0.75; // 4:3
  const RATIO_TOLERANCE = 0.05; // an image within 5% of 4:3 is framed as 4:3
  const PHONE_SPEED = 450; // ms per photo change on phones; product-redesigned.css uses the same duration and curve
  const SHIFT_MS = 140; // wheel slide animation
  const WHEEL_DETENT_PX = 240; // wheel travel held at the thumbnails before the page scrolls on
  const HEADER_FOLD_AT = 0.8; // share of the wheel slide at which the header compacts
  const SWIPE_PX = 40; // sideways trackpad travel that changes the photo
  const SWIPE_IDLE_MS = 180; // a pause this long ends a trackpad gesture

  // Frame height / width for a slide: 4:3 unless it's an image whose ratio is more than 5% off 4:3. The app's inline
  // padding-bottom carries the media ratio before the image loads; the image's own size is the fallback.
  function slideRatio(slide) {
    if (!slide || slide.querySelector('iframe, video')) return RATIO;
    let r = parseFloat(slide.style.paddingBottom) / 100;
    const img = slide.querySelector('img');
    if (!(r > 0) && img && img.naturalWidth) r = img.naturalHeight / img.naturalWidth;
    return r > 0 && Math.abs(r / RATIO - 1) > RATIO_TOLERANCE ? r : RATIO;
  }
  // The thumbnail strip (the element that scrolls) and the box around it. The app's generated class names (pwzrjss2…)
  // are numbered at runtime and change between page loads, so find them by its stable class names and tag the box
  // with our own class for the CSS.
  function stripParts(root) {
    const strip = root && root.querySelector('.PwzrGrid-root.rio-hide-scrollbar');
    if (!strip) return {};
    // The scrolling grid sits in a same-size .rio-hide-scrollbar box: that box holds the strip arrows.
    const wrap = strip.parentElement.closest('.rio-hide-scrollbar') || strip.parentElement;
    wrap.classList.add('awzz-strip-wrap');
    return { wrap, strip };
  }

  // One entry per live gallery; page-level listeners (below) loop over them, so nothing piles up on variant changes.
  const fitters = new Set();
  const each = fn => fitters.forEach(f => (f.container.isConnected ? fn(f) : (f.ctl.abort(), fitters.delete(f))));
  // The header shrinks as the page scrolls; one observer re-pins every gallery under it.
  let header = null;
  const headerHeight = () => (header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0);
  function watchHeader() {
    const h = document.querySelector('.site-header');
    if (!h || h === header) return;
    header = h;
    if (window.ResizeObserver) new ResizeObserver(() => each(f => { f.stick(); f.relax(); })).observe(h);
  }

  // While the wheel slides the gallery at the top of the page, the page doesn't scroll, so the theme never compacts the
  // header. Do what the theme does on scroll (empire.js StickyHeader: same classes and margin, same transition) so the
  // menu row folds away as the gallery nears its bottom and comes back when it's back at the top. The theme's own
  // scroll handling keeps working: it reads these same classes.
  const headerNav = () => header && header.querySelector('[data-site-navigation]');
  const headerCompact = () => !header || header.classList.contains('site-header-sticky--scrolled');
  // The menu row's own transition length (300–400ms with the smoother header PR, 150ms in the stock theme).
  const navMs = () => {
    const nav = headerNav();
    return nav ? (parseFloat(getComputedStyle(nav).transitionDuration) || 0) * 1000 : 0;
  };
  // empire.js keeps the header's height in --header-height (the sticky section tabs sit under it); keep it current.
  const storeHeaderHeight = () => {
    if (header) document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  };
  // The announcement bars above the header scroll away with the page; here the page doesn't move, so they fold
  // (height to 0) on the menu row's timing and unfold when the header opens again. Folded bars are inert.
  const bars = () => Array.from(document.querySelectorAll('.shopify-section-group-header-group'))
    .filter(el => header && el.compareDocumentPosition(header) & Node.DOCUMENT_POSITION_FOLLOWING && !el.contains(header));
  let barsFolded = false;
  function foldBars(on) {
    if (barsFolded === on) return;
    barsFolded = on;
    const ms = REDUCED.matches ? 0 : navMs();
    bars().forEach(el => {
      const h = el.scrollHeight;
      el.inert = on;
      el.style.overflow = 'hidden';
      el.style.transition = ms ? `height ${ms}ms cubic-bezier(.2, 0, 0, 1)` : 'none';
      el.style.height = (on ? h : 0) + 'px';
      el.getBoundingClientRect(); // start from the current height
      el.style.height = (on ? 0 : h) + 'px';
      if (!on) {
        const done = () => { if (!barsFolded) { el.style.height = ''; el.style.overflow = ''; el.style.transition = ''; } };
        if (!ms || !h) done();
        else el.addEventListener('transitionend', function end(e) {
          if (e.target !== el) return;
          el.removeEventListener('transitionend', end);
          done();
        });
      }
    });
  }
  function compactHeader(on) {
    const nav = headerNav();
    if (!nav || !document.body.classList.contains('site-header-sticky')) return;
    // Bars only at the very top: folding them lower lets the browser's scroll anchoring move the page.
    foldBars(on ? window.scrollY < 1 : false);
    if (headerCompact() === on) return;
    if (on) {
      const done = e => {
        if (e && e.target !== nav) return;
        nav.removeEventListener('transitionend', done);
        if (header.classList.contains('site-header-sticky--scrolled')) header.classList.remove('site-header-nav--open');
        storeHeaderHeight();
      };
      if (navMs()) nav.addEventListener('transitionend', done);
      nav.setAttribute('style', `margin-top: -${nav.getBoundingClientRect().height}px;`);
      header.classList.add('site-header-sticky--scrolled');
      if (!navMs()) done();
    } else {
      header.classList.add('site-header-nav--open');
      header.classList.remove('site-header-sticky--scrolled');
      header.querySelector('[data-menu-toggle]')?.classList.remove('active');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        nav.setAttribute('style', 'margin-top: 0;');
        setTimeout(storeHeaderHeight, navMs() + 50);
      }));
    }
  }
  // The page is in the theme's "header expanded" zone (above its intersection target).
  const pageAtTop = () => {
    const t = document.querySelector('[data-header-intersection-target]');
    return !t || t.getBoundingClientRect().bottom > 0;
  };

  function fit(container) {
    const swiper = container.swiper;
    const root = container.closest('.rio-media-gallery');
    if (!root) return;
    const ctl = new AbortController();
    const on = { signal: ctl.signal };
    // The block (photo + thumbnails) may outlive the container across a rebuild: drop the old block listeners.
    let block = container;
    while (block.parentElement && block.parentElement !== root) block = block.parentElement;
    if (block.awzzCtl) block.awzzCtl.abort();
    block.awzzCtl = ctl;

    // Marks the render on its slide and its thumbnail, which takes the render's own shape (square); the others stay
    // 4:3. Run once, then again only after images load, at most once a frame.
    function tag() {
      if (!swiper.slides || !container.isConnected) return; // the app destroyed this Swiper (variant change)
      swiper.slides.forEach(s => s.classList.toggle('awzz-render', slideRatio(s) !== RATIO));
      const real = Array.from(swiper.slides).filter(s => !s.classList.contains(swiper.params.slideDuplicateClass));
      root.querySelectorAll('.PwzrGrid-item').forEach((item, i) => {
        const r = slideRatio(real[i]);
        item.classList.toggle('awzz-thumb-render', r !== RATIO);
        if (r !== RATIO) item.style.setProperty('--awzz-thumb-ar', `1 / ${r}`);
      });
    }
    let tagQueued = false;
    const onLoad = () => {
      if (tagQueued) return;
      tagQueued = true;
      requestAnimationFrame(() => { tagQueued = false; tag(); size(); });
    };

    // Desktop (990px+): the frame is as wide as the column, narrower on short screens so photo + thumbnails fit under
    // the collapsed header. Phones (under 720px): full width. Tablets keep the app's sizing. The height change
    // animates in CSS; the app's per-slide padding-bottom ratio box is overridden there (.awzz-fit).
    function size() {
      if (!container.isConnected) return;
      const fitOn = DESK.matches || PHONE.matches;
      root.classList.toggle('awzz-fit', fitOn);
      root.classList.toggle('awzz-fit-desk', DESK.matches);
      if (!fitOn) {
        root.style.removeProperty('--awzz-frame-w');
        root.style.removeProperty('--awzz-frame-h');
        container.style.height = '';
        stick();
        return;
      }
      let w = '';
      if (DESK.matches) {
        const { wrap } = stripParts(root);
        const navH = wrap ? wrap.offsetHeight + parseFloat(getComputedStyle(wrap).marginTop) : 0;
        w = Math.floor(Math.min(root.clientWidth, (window.innerHeight - HDR - 2 * GAP - navH) / RATIO)) + 'px';
      }
      if (root.style.getPropertyValue('--awzz-frame-w') !== w) {
        if (w) root.style.setProperty('--awzz-frame-w', w); else root.style.removeProperty('--awzz-frame-w');
        swiper.update(); // Swiper measures slide widths from the container
      }
      const target = Math.round(container.clientWidth * slideRatio(swiper.slides[swiper.activeIndex]));
      container.style.height = target + 'px';
      // Kept on the gallery so the Swiper the app rebuilds on a variant change starts at this height instead of 0.
      root.style.setProperty('--awzz-frame-h', target + 'px');
      stick();
    }

    // Desktop with a mouse or trackpad: the block stays under the sticky header while the buy box scrolls. Touch
    // screens (an iPad in landscape) have no wheel to reach thumbnails below the fold, so they keep the normal flow.
    // Supersedes the 82px / 860px-tall rule that was in static-product-redesigned.
    const pinned = () => DESK.matches && FINE.matches;
    function stick() {
      if (!pinned()) {
        block.style.position = '';
        block.style.top = '';
        if (shift) shiftTo(0);
        return;
      }
      block.style.position = 'sticky';
      block.style.top = headerHeight() + GAP + 'px';
    }

    // When the block is taller than the screen (the square render), the mouse wheel over it slides only the gallery up
    // until the thumbnails show, and the buy box stays put. A transform, not a scroll container, which would clip the
    // frame's ring.
    let shift = 0;
    // The block's box without the shift, read from the transform actually painted (mid-animation it isn't `shift`).
    function natural() {
      const r = block.getBoundingClientRect();
      const t = getComputedStyle(block).transform;
      const dy = t && t !== 'none' ? new DOMMatrix(t).m42 : 0;
      return { top: r.top - dy, bottom: r.bottom - dy };
    }
    function room() {
      if (!pinned()) return 0;
      return Math.max(0, natural().bottom - (window.innerHeight - GAP));
    }
    function shiftTo(s, animate) {
      shift = s;
      block.style.transition = animate && !REDUCED.matches ? `transform ${SHIFT_MS}ms cubic-bezier(.2, .8, .2, 1)` : 'none';
      block.style.transform = s ? `translateY(${-s}px)` : '';
    }
    // While the pointer is over a gallery taller than the screen, the wheel moves the gallery first:
    // - down: the gallery slides until the thumbnails show and stops there (a short detent: the page doesn't jump on);
    //   keep wheeling past the detent and the page scrolls on;
    // - up: the gallery slides back until the photo's top shows, then the page scrolls up as usual.
    // The page also scrolls with the wheel over the buy box, the keyboard, the scrollbar and touch.
    let overflow = 0;
    block.addEventListener('wheel', e => {
      if (e.ctrlKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return; // pinch zoom and sideways swipes pass
      const max = room();
      if (!max && !shift) return; // it fits: the page scrolls as usual
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      if (dy < 0 && !shift) return; // photo top showing: scrolling up is the page's
      if (dy > 0 && shift >= max - 2) { // at the thumbnails: hold for the detent, then hand over to the page
        overflow += dy;
        if (overflow > WHEEL_DETENT_PX) return;
        e.preventDefault();
        return;
      }
      if (dy < 0) overflow = 0;
      e.preventDefault();
      shiftTo(Math.max(0, Math.min(max, shift + dy)), true);
      // Near the bottom of the slide the header compacts; back at the top (and only in the theme's top zone) it opens.
      if (pageAtTop()) {
        if (shift >= max * HEADER_FOLD_AT) compactHeader(true);
        else if (shift === 0) compactHeader(false);
      }
    }, { passive: false, signal: ctl.signal });
    // Trackpad: a sideways two-finger swipe over the photo changes it, one photo per gesture (the gesture's momentum
    // keeps sending wheel events, so the next photo waits until they stop). Also stops the browser's swipe-to-go-back.
    let swipeX = 0, swipeLock = 0;
    container.addEventListener('wheel', e => {
      if (e.ctrlKey || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      clearTimeout(swipeLock);
      swipeLock = setTimeout(() => { swipeX = 0; swipeLock = 0; }, SWIPE_IDLE_MS);
      if (swipeX === null) return; // this gesture already changed the photo
      swipeX += e.deltaX;
      if (Math.abs(swipeX) < SWIPE_PX) return;
      if (swipeX > 0) swiper.slideNext(); else swiper.slidePrev();
      swipeX = null;
    }, { passive: false, signal: ctl.signal });
    // Keyboard: a focused thumbnail below the screen slides the gallery up so it's visible.
    block.addEventListener('focusin', e => {
      const max = room();
      if (max && e.target.closest('.awzz-strip-wrap')) shiftTo(max, false);
    }, on);
    // Scrolling the page or a shorter photo needs less shift.
    const relax = () => { if (shift) shiftTo(Math.min(shift, room())); };
    container.addEventListener('transitionend', e => { if (e.target === container) relax(); }, on);
    container.addEventListener('load', e => {
      if (e.target.tagName === 'IMG') e.target.classList.add('awzz-loaded');
      onLoad(); // an image without the ratio box
    }, { capture: true, signal: ctl.signal });
    container.querySelectorAll('.pwzrswiper-slide img').forEach(img => {
      if (img.complete && img.naturalWidth) img.classList.add('awzz-loaded');
    });
    // The photo on screen, kept on the gallery root (which survives the app's rebuild) as the frame's backdrop.
    // Only a photo that has finished loading is kept: a source that is still downloading would paint nothing (white).
    const remember = () => {
      if (!swiper.slides) return; // the app destroyed this Swiper (variant change)
      const img = swiper.slides[swiper.activeIndex]?.querySelector('img');
      if (!img) return;
      const keep = () => {
        const src = img.currentSrc || img.src;
        if (src && container.isConnected) root.style.setProperty('--awzz-last', `url("${src}")`);
      };
      if (img.complete && img.naturalWidth) keep();
      else img.addEventListener('load', keep, { once: true });
    };
    swiper.on('slideChange', remember);
    setTimeout(remember, 600); // once the first photo has a source

    // Photo changes: 450ms on the standard ease curve on phones, none with reduced motion. While a photo moves (finger
    // or glide), the frame's height is read off the photo's painted position every frame and blended between the two
    // photos' heights, so photo, frame and the page below move as one.
    const speed = swiper.params.speed;
    const setSpeed = () => { swiper.params.speed = REDUCED.matches ? 0 : PHONE.matches ? PHONE_SPEED : speed; };
    const wrapper = container.querySelector('.pwzrswiper-wrapper');
    let following = 0, followUntil = 0;
    function followFrame() {
      if (!container.isConnected) { following = 0; return; }
      const w = swiper.width || container.clientWidth;
      const t = getComputedStyle(wrapper).transform;
      const tx = t && t !== 'none' ? new DOMMatrix(t).m41 : swiper.translate;
      const x = Math.max(0, Math.min(swiper.slides.length - 1, -tx / w));
      const i = Math.floor(x), f = x - i;
      const h0 = w * slideRatio(swiper.slides[i]);
      const h1 = swiper.slides[i + 1] ? w * slideRatio(swiper.slides[i + 1]) : h0;
      container.style.transition = 'none';
      container.style.height = Math.round(h0 + (h1 - h0) * f) + 'px';
      if (performance.now() < followUntil) following = requestAnimationFrame(followFrame);
      else { following = 0; container.style.transition = ''; size(); }
    }
    const follow = ms => {
      if (!PHONE.matches || REDUCED.matches) return;
      followUntil = Math.max(followUntil, performance.now() + ms);
      if (!following) following = requestAnimationFrame(followFrame);
    };
    swiper.on('sliderMove', () => follow(10000)); // a sideways drag, until the finger lifts (not page scrolls)
    swiper.on('touchEnd', () => { followUntil = 0; follow(PHONE_SPEED + 100); });
    swiper.on('transitionStart', () => follow(PHONE_SPEED + 100));
    swiper.on('slideChange', () => { if (!following) size(); });

    fitters.add({ container, ctl, size, stick, relax, setSpeed, unshift: () => { if (shift) shiftTo(0, true); } });
    setSpeed();
    tag();
    size();
  }

  let resizeQueued = false;
  window.addEventListener('resize', () => {
    if (resizeQueued) return;
    resizeQueued = true;
    requestAnimationFrame(() => { resizeQueued = false; each(f => f.size()); syncStrips(); });
  });
  // Back at the very top of the page (the gallery's own wheel handling aside), everything the wheel slide changed comes
  // back: the gallery returns to its place, the announcement bars and the menu row open. Covers scrolling up over the
  // buy box after sliding the gallery, where the page itself can't move any further.
  function restoreTop() {
    each(f => f.unshift());
    compactHeader(false);
  }
  window.addEventListener('scroll', () => {
    each(f => f.relax());
    if (barsFolded && window.scrollY < 1) restoreTop();
  }, { passive: true });
  window.addEventListener('wheel', e => {
    if (!e.defaultPrevented && e.deltaY < 0 && window.scrollY < 1 && barsFolded) restoreTop();
  }, { passive: true });
  [PHONE, REDUCED].forEach(mq => mq.addEventListener('change', () => each(f => f.setSpeed())));
  FINE.addEventListener('change', () => each(f => f.size()));

  // After a variant change the app shows that variant's photos, downloaded only then. Once the page is idle, the first
  // photo of each other colour in the selected size is fetched ahead, at the size the gallery uses, so switching colour
  // shows it at once. The app's own data (variant → media order) decides which photo that is.
  const preloaded = new Set();
  function preloadColours(container) {
    const rio = window.__productWizRio, product = rio && rio.product;
    const form = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (!product || !rio.productRio || !form) return;
    const net = navigator.connection;
    if (net && (net.saveData || /(^|-)(2|3)g$/.test(net.effectiveType || ''))) return; // data saver or slow network
    const colour = product.options.findIndex(o => /colou?r|finish/i.test(o.name || o));
    const current = product.variants.find(v => String(v.id) === form.value);
    if (!container.isConnected || !container.swiper?.slides) return; // gallery rebuilt before this idle callback ran
    const active = container.swiper.slides[container.swiper.activeIndex]?.querySelector('img');
    const width = active && /[?&]width=(\d+)/.exec(active.currentSrc || active.src);
    if (colour < 0 || !current || !width) return;
    product.variants
      .filter(v => v.id !== current.id && v.options.every((o, i) => i === colour || o === current.options[i]))
      .forEach(v => {
        const first = rio.productRio.find(x => String(x.id) === String(v.id))?.media?.[0];
        const media = first && product.media.find(m => String(m.id) === String(first));
        const src = media && (media.preview_image?.src || media.src);
        if (!src) return;
        const url = `${src}${src.includes('?') ? '&' : '?'}width=${width[1]}`;
        if (preloaded.has(url)) return;
        preloaded.add(url);
        new Image().src = url;
      });
  }
  const whenIdle = fn => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 1500));

  function attach(container) {
    const swiper = container.swiper;
    if (!swiper || container.querySelector('.awzz-gallery-counter')) return !!swiper;
    if (!swiper.initialized) return false;
    watchHeader();
    fit(container);
    // Loop mode adds clones; the app's Swiper uses its own class prefix, so read the clone class from params.
    const total = Array.from(swiper.slides).filter(s => !s.classList.contains(swiper.params.slideDuplicateClass)).length;
    if (total < 2) return true;

    // "3 / 70" on screen; screen readers hear "Photo 3 of 70" when the photo changes.
    const counter = document.createElement('span');
    counter.className = 'awzz-gallery-counter';
    counter.setAttribute('aria-hidden', 'true');
    const status = document.createElement('span');
    status.className = 'awzz-sr';
    status.setAttribute('role', 'status');
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'awzz-gallery-arrow awzz-gallery-prev';
    prev.setAttribute('aria-label', 'Previous product photo');
    prev.innerHTML = CHEVRON;
    const next = prev.cloneNode();
    next.className = 'awzz-gallery-arrow awzz-gallery-next';
    next.setAttribute('aria-label', 'Next product photo');
    next.innerHTML = CHEVRON;
    // stopPropagation: a click on the frame opens the app's full-screen viewer.
    prev.addEventListener('click', e => { e.stopPropagation(); swiper.slidePrev(); });
    next.addEventListener('click', e => { e.stopPropagation(); swiper.slideNext(); });

    const update = () => {
      const n = swiper.realIndex + 1;
      counter.textContent = `${n} / ${total}`;
      status.textContent = `Photo ${n} of ${total}`;
      // 3D and video slides carry their own captions and controls in that corner, so the counter steps aside.
      counter.hidden = !!swiper.slides[swiper.activeIndex]?.querySelector('iframe, video, model-viewer');
      // With loop on there is no end; without it, disable at first/last.
      prev.disabled = !swiper.params.loop && swiper.isBeginning;
      next.disabled = !swiper.params.loop && swiper.isEnd;
    };
    container.append(prev, next, counter, status);
    const root = container.closest('.rio-media-gallery');
    swiper.on('slideChange', () => { update(); centerThumb(root, swiper.realIndex); });
    update();
    stripArrows(root);
    centerThumb(root, swiper.realIndex);
    whenIdle(() => preloadColours(container));
    return true;
  }

  // The app keeps the active thumbnail in view by stepping the strip's scrollLeft in a few rough jumps. This one strip
  // element ignores scrollLeft writes (only the app's code writes them); on each photo change the active thumbnail is
  // centred with one native smooth scroll instead. Finger, trackpad and strip-arrow scrolls are native and unaffected.
  const LEFT = Object.getOwnPropertyDescriptor(Element.prototype, 'scrollLeft');
  function quietStrip(strip) {
    if (!LEFT || strip.awzzQuiet) return;
    strip.awzzQuiet = true;
    Object.defineProperty(strip, 'scrollLeft', { configurable: true, get() { return LEFT.get.call(this); }, set() {} });
  }
  function centerThumb(root, index) {
    const { strip } = stripParts(root);
    const item = strip && strip.querySelectorAll('.PwzrGrid-item')[index];
    if (!item) return;
    const s = strip.getBoundingClientRect(), r = item.getBoundingClientRect();
    const left = strip.scrollLeft + r.left - s.left - (strip.clientWidth - r.width) / 2;
    strip.scrollTo({ left, behavior: REDUCED.matches ? 'auto' : 'smooth' });
  }

  // Strip arrows (desktop): one at each end, shown only when the strip can scroll that way.
  const strips = new Set();
  const syncStrips = () => strips.forEach(s => (s.strip.isConnected ? s.sync() : strips.delete(s)));
  function stripArrows(root) {
    const { wrap, strip } = stripParts(root);
    if (strip) quietStrip(strip);
    if (!strip || wrap.querySelector('.awzz-strip-arrow')) return;
    const make = (dir, label) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `awzz-strip-arrow awzz-strip-${dir}`;
      b.setAttribute('aria-label', label);
      b.innerHTML = CHEVRON_SM;
      b.addEventListener('click', () => strip.scrollBy({
        left: (dir === 'next' ? 1 : -1) * strip.clientWidth * 0.8,
        behavior: REDUCED.matches ? 'auto' : 'smooth',
      }));
      return b;
    };
    const back = make('prev', 'Previous photos'), more = make('next', 'More photos');
    const sync = () => {
      back.hidden = strip.scrollLeft <= 1;
      more.hidden = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
    };
    if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
    wrap.append(back, more);
    strip.addEventListener('scroll', sync, { passive: true });
    strips.add({ strip, sync });
    sync();
  }

  // The pre-order / build-to-order ribbon (aw-preorder.js) is added to the gallery root, which isn't sticky; move it
  // into the photo frame so it stays on the frame's corner and is clipped by its rounding. aw-preorder.js re-adds it
  // only when it's missing from the page, so moving it is safe.
  function adoptBadges(host) {
    host.querySelectorAll('.rio-media-gallery > [data-aw-preorder-badge]').forEach(badge => {
      const frame = badge.parentElement.querySelector('.pwzrswiper-container');
      if (frame) frame.append(badge);
    });
  }
  // The app rebuilds the gallery (new Swiper) when the variant changes, so watch for new containers and retry each
  // one until its Swiper has initialized (up to 5s).
  const seen = new WeakSet();
  // Variant change without a white frame. The app removes the old gallery as soon as an option is picked and builds
  // the new one a few frames later; its photo then downloads and fades in. Two layers cover that gap:
  // - from the click itself, a copy of the photo on screen (already decoded) sits over the frame and fades out once
  //   the new variant's photo has faded in (a CSS background could still paint white while it decodes);
  // - the last photo also stays painted under the new frame (.awzz-handover) as a backstop.
  // Each hold starts a new turn, so an older timer can't release a newer hold; the fallback only fires if the new
  // photo never loads. A pick that doesn't rebuild the gallery (same variant) is released after 1.2s.
  function holdLast(root, fromClick) {
    const frame = root.querySelector('.pwzrswiper-container');
    const turn = String(Date.now() + Math.random());
    root.dataset.awzzTurn = turn;
    if (root.style.getPropertyValue('--awzz-last')) root.classList.add('awzz-handover');
    const shown = frame?.swiper ? frame.swiper.slides[frame.swiper.activeIndex]?.querySelector('img') : null;
    if (fromClick && shown && shown.complete && shown.naturalWidth && !root.querySelector(':scope > .awzz-ghost')) {
      const box = frame.getBoundingClientRect(), base = root.getBoundingClientRect();
      if (getComputedStyle(root).position === 'static') root.style.position = 'relative';
      const ghost = document.createElement('div');
      ghost.className = 'awzz-ghost';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.style.cssText = `left:${box.left - base.left}px;top:${box.top - base.top}px;width:${box.width}px;height:${box.height}px`;
      const copy = new Image();
      copy.alt = '';
      copy.src = shown.currentSrc || shown.src;
      copy.style.objectFit = getComputedStyle(shown).objectFit || 'contain';
      ghost.append(copy);
      root.append(ghost);
      root.classList.add('awzz-ghosting');
    }
    // The copy fades out as soon as the new photo is loaded (that photo is shown at once underneath it, so the two
    // cross-fade with no white in between); the backdrop goes once a photo fading in on its own is fully in.
    const release = () => {
      if (root.dataset.awzzTurn !== turn) return;
      root.querySelectorAll(':scope > .awzz-ghost').forEach(g => {
        g.classList.add('awzz-ghost-out');
        setTimeout(() => {
          g.remove();
          if (!root.querySelector(':scope > .awzz-ghost')) root.classList.remove('awzz-ghosting');
        }, 250);
      });
      setTimeout(() => { if (root.dataset.awzzTurn === turn) root.classList.remove('awzz-handover'); }, 300);
    };
    setTimeout(release, 4000);
    if (fromClick) setTimeout(() => { if (root.querySelector('.pwzrswiper-container') === frame) release(); }, 1200);
    return release;
  }
  document.addEventListener('click', e => {
    if (!e.target.closest || !e.target.closest('.rio-options')) return;
    document.querySelectorAll('.rio-media-gallery').forEach(root => holdLast(root, true));
  }, true);
  function scan(host) {
    adoptBadges(host);
    host.querySelectorAll('.rio-media-gallery .pwzrswiper-container').forEach(container => {
      if (seen.has(container)) return;
      seen.add(container);
      // Photos fade in as soon as they load, even before the controls below attach.
      // A rebuilt gallery shows the previous photo underneath until its own active photo has faded in (or after 2s).
      // The flag sits on the gallery root: the app re-renders the container's classes, and dropping the backdrop
      // while the new photo is still fading in showed a white frame between the two photos.
      const root = container.closest('.rio-media-gallery');
      const release = root ? holdLast(root, false) : () => {};
      let handedOver = false;
      const handover = () => { handedOver = true; release(); };
      const shown = img => {
        img.classList.add('awzz-loaded');
        if (img.closest('.pwzrswiper-slide-active')) handover();
      };
      container.addEventListener('load', e => { if (e.target.tagName === 'IMG') shown(e.target); }, true);
      container.querySelectorAll('img').forEach(img => { if (img.complete && img.naturalWidth) shown(img); });
      // A photo can finish loading (often from the cache) before Swiper marks its slide active. Its load event has then
      // already passed, so also check the active slide while the gallery sets up; otherwise the previous photo stays
      // over the new one until the 4s fallback.
      const activeLoaded = () => {
        const img = container.querySelector('.pwzrswiper-slide-active img');
        if (img && img.complete && img.naturalWidth) shown(img);
      };
      let tries = 0, attached = false;
      const iv = setInterval(() => {
        if (!attached) attached = attach(container);
        if (!handedOver) activeLoaded();
        if ((attached && handedOver) || ++tries > 50 || !container.isConnected) clearInterval(iv);
      }, 100);
    });
  }
  // Only the product area is watched (the theme renders .product-gallery; the app draws inside it), at most once a
  // frame.
  const host = document.querySelector('.product--outer') || document.querySelector('.product-gallery')?.parentElement;
  if (!host) return;
  let scanQueued = false;
  new MutationObserver(() => {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => { scanQueued = false; scan(host); });
  }).observe(host, { childList: true, subtree: true });
  scan(host);
})();
